/**
 * Bitboard-based solver for fast uniqueness checking.
 *
 * Supports grids where both dimensions ≤ 32.  Each row/column state is
 * packed into a single uint32, making constraint propagation and
 * backtracking dramatically faster than the cell-by-cell approach.
 *
 * The core idea: enumerate all valid clue placements compatible with
 * the current known cells, computing AND (must-fill) and OR (may-fill)
 * on the fly.  Cells in AND are forced filled; cells not in OR are
 * forced empty.  This replaces the chain of four separate line-solving
 * techniques with a single, more powerful pass.
 */

import type {
  Puzzle,
  PuzzleClues,
  SolutionAnalysisOptions,
  SolutionAnalysisResult,
} from '../types.js';

// ── Bit utilities ────────────────────────────────────────────────────

/** Mask with bits 0..n-1 set.  Handles n = 32 (returns 0xFFFFFFFF). */
function allBits(n: number): number {
  return n >= 32 ? -1 : (1 << n) - 1;
}

/** Count trailing zeros (position of lowest set bit).  Returns 32 for 0. */
function ctz(x: number): number {
  if (x === 0) return 32;
  return 31 - Math.clz32(x & -x);
}

/** Population count (Hamming weight). */
function popcount(x: number): number {
  x = x - ((x >>> 1) & 0x55555555);
  x = (x & 0x33333333) + ((x >>> 2) & 0x33333333);
  return (((x + (x >>> 4)) & 0x0f0f0f0f) * 0x01010101) >>> 24;
}

// ── BitGrid ──────────────────────────────────────────────────────────

/**
 * Compact grid representation.
 *   filled[r] : bit c set ⟹ cell (r, c) is filled
 *   known[r]  : bit c set ⟹ cell (r, c) is determined
 *   Unknown = ¬known, Empty = known ∧ ¬filled, Filled = known ∧ filled
 */
interface BitGrid {
  filled: Uint32Array;
  known: Uint32Array;
  rows: number;
  cols: number;
}

function createBitGrid(rows: number, cols: number): BitGrid {
  return {
    filled: new Uint32Array(rows),
    known: new Uint32Array(rows),
    rows,
    cols,
  };
}

function cloneBitGrid(g: BitGrid): BitGrid {
  return {
    filled: new Uint32Array(g.filled),
    known: new Uint32Array(g.known),
    rows: g.rows,
    cols: g.cols,
  };
}

// ── Line solver ──────────────────────────────────────────────────────

/**
 * Solve a single line by enumerating all clue placements compatible
 * with the current known cells, computing forced cells via AND / OR.
 *
 * Returns { mustFill, mustEmpty } bitmasks of *new* deductions, or
 * null when no compatible placement exists (contradiction).
 */
function solveLineBits(
  clues: number[],
  lineLen: number,
  filledMask: number,
  knownMask: number,
): { mustFill: number; mustEmpty: number } | null {
  const valid = allBits(lineLen);

  // All-empty clue ([0])
  if (clues.length === 1 && clues[0] === 0) {
    if (filledMask & knownMask & valid) return null; // known-filled contradicts
    return { mustFill: 0, mustEmpty: ~knownMask & valid };
  }

  const emptyMask = knownMask & ~filledMask & valid; // known-empty cells
  const reqFilled = filledMask & knownMask & valid; // known-filled cells
  const numGroups = clues.length;

  // Minimum space from group i to end of line
  const minFrom = new Int32Array(numGroups + 1);
  for (let i = numGroups - 1; i >= 0; i--) {
    minFrom[i] = clues[i] + (i < numGroups - 1 ? 1 + minFrom[i + 1] : 0);
  }

  let andAll = valid;
  let orAll = 0;
  let found = false;
  const unknownMask = ~knownMask & valid;
  let settled = false; // true when no more deductions are possible

  // Recursive placement enumeration with pruning
  const recurse = (g: number, pos: number, mask: number): void => {
    if (settled) return;

    if (g === numGroups) {
      // Verify no uncovered known-filled cells after last group
      const tailBits = pos < 32 ? valid & ~allBits(pos) : 0;
      if (reqFilled & tailBits & ~mask) return;
      andAll &= mask;
      orAll |= mask;
      found = true;
      // Early exit: AND collapsed & OR saturated → no more deductions
      if ((andAll & unknownMask) === 0 && (~orAll & unknownMask) === 0) {
        settled = true;
      }
      return;
    }

    const groupLen = clues[g];
    const maxStart = lineLen - minFrom[g];

    for (let s = pos; s <= maxStart; s++) {
      if (settled) return;

      // Gap [pos, s): no known-filled cells allowed (they'd be uncovered)
      if (s > pos) {
        const gapBits = allBits(s - pos) << pos;
        if (reqFilled & gapBits) return; // dead end for this branch
      }

      // Group [s, s+groupLen): no known-empty cells allowed
      const groupBits = allBits(groupLen) << s;
      const conflict = emptyMask & groupBits;
      if (conflict) {
        // Skip past the rightmost empty cell in the group range
        s = 31 - Math.clz32(conflict);
        continue;
      }

      // Separator: cell right after group must not be known-filled
      const sepPos = s + groupLen;
      if (sepPos < lineLen && (reqFilled & (1 << sepPos))) {
        continue;
      }

      recurse(g + 1, sepPos + 1, mask | groupBits);
    }
  };

  recurse(0, 0, 0);

  if (!found) return null;

  return {
    mustFill: andAll & ~knownMask & valid,
    mustEmpty: ~orAll & ~knownMask & valid,
  };
}

// ── Column helpers ───────────────────────────────────────────────────

function extractCol(
  grid: BitGrid,
  c: number,
): { filled: number; known: number } {
  let filled = 0;
  let known = 0;
  const bit = 1 << c;
  for (let r = 0; r < grid.rows; r++) {
    if (grid.filled[r] & bit) filled |= 1 << r;
    if (grid.known[r] & bit) known |= 1 << r;
  }
  return { filled, known };
}

/** Apply column deductions, returns bitmask of affected rows. */
function applyColDeductions(
  grid: BitGrid,
  c: number,
  mustFill: number,
  mustEmpty: number,
): number {
  const bit = 1 << c;
  const allNew = mustFill | mustEmpty;
  let affected = 0;
  let bits = allNew;
  while (bits) {
    const r = ctz(bits);
    bits &= bits - 1;
    if (mustFill & (1 << r)) grid.filled[r] |= bit;
    grid.known[r] |= bit;
    affected |= 1 << r;
  }
  return affected;
}

// ── Propagation ──────────────────────────────────────────────────────

type PropResult = 'solved' | 'stuck' | 'contradiction';

function propagate(grid: BitGrid, clues: PuzzleClues): PropResult {
  const { rows, cols } = grid;
  const colsMask = allBits(cols);

  // Dirty-line tracking via bitmasks (one bit per row/col)
  let dirtyRows = allBits(rows);
  let dirtyCols = allBits(cols);

  while (dirtyRows | dirtyCols) {
    // ── Row pass ──
    let pending = dirtyRows;
    dirtyRows = 0;

    while (pending) {
      const r = ctz(pending);
      pending &= pending - 1;

      const result = solveLineBits(
        clues.rows[r],
        cols,
        grid.filled[r],
        grid.known[r],
      );
      if (!result) return 'contradiction';

      const newBits = result.mustFill | result.mustEmpty;
      if (newBits) {
        grid.filled[r] |= result.mustFill;
        grid.known[r] |= newBits;
        dirtyCols |= newBits;
      }
    }

    // ── Column pass ──
    pending = dirtyCols;
    dirtyCols = 0;

    while (pending) {
      const c = ctz(pending);
      pending &= pending - 1;

      const { filled, known } = extractCol(grid, c);
      const result = solveLineBits(clues.cols[c], rows, filled, known);
      if (!result) return 'contradiction';

      const newBits = result.mustFill | result.mustEmpty;
      if (newBits) {
        dirtyRows |= applyColDeductions(
          grid,
          c,
          result.mustFill,
          result.mustEmpty,
        );
      }
    }
  }

  // Check solved
  for (let r = 0; r < rows; r++) {
    if ((grid.known[r] & colsMask) !== colsMask) return 'stuck';
  }
  return 'solved';
}

// ── Branch-cell selection ────────────────────────────────────────────

function findBranchCell(
  grid: BitGrid,
): { row: number; col: number } | null {
  const { rows, cols } = grid;
  const colsMask = allBits(cols);

  // Unknowns per row (via popcount)
  const rowUnk = new Int32Array(rows);
  for (let r = 0; r < rows; r++) {
    rowUnk[r] = popcount(~grid.known[r] & colsMask);
  }

  // Unknowns per column
  const colUnk = new Int32Array(cols);
  for (let c = 0; c < cols; c++) {
    const bit = 1 << c;
    let count = 0;
    for (let r = 0; r < rows; r++) {
      if (!(grid.known[r] & bit)) count++;
    }
    colUnk[c] = count;
  }

  let bestRow = -1;
  let bestCol = -1;
  let bestScore = Infinity;

  for (let r = 0; r < rows; r++) {
    if (rowUnk[r] === 0) continue;
    if (rowUnk[r] >= bestScore) continue; // can't beat best

    let bits = ~grid.known[r] & colsMask;
    while (bits) {
      const c = ctz(bits);
      bits &= bits - 1;

      const score = rowUnk[r] + colUnk[c];
      if (score < bestScore) {
        bestScore = score;
        bestRow = r;
        bestCol = c;
      }
    }
  }

  return bestRow >= 0 ? { row: bestRow, col: bestCol } : null;
}

// ── Backtracking ─────────────────────────────────────────────────────

interface SearchState {
  limit: number;
  maxStates: number;
  exploredStates: number;
  aborted: boolean;
}

function countSolutions(
  grid: BitGrid,
  clues: PuzzleClues,
  search: SearchState,
): number {
  if (search.aborted) return 0;

  const cell = findBranchCell(grid);
  if (!cell) return 1;

  let count = 0;
  const { row, col } = cell;
  const bit = 1 << col;

  // Try Filled first, then Empty
  for (let tryFilled = 1; tryFilled >= 0; tryFilled--) {
    if (search.aborted || count >= search.limit) break;
    if (search.exploredStates >= search.maxStates) {
      search.aborted = true;
      break;
    }
    search.exploredStates++;

    const branch = cloneBitGrid(grid);
    if (tryFilled) branch.filled[row] |= bit;
    branch.known[row] |= bit;

    const result = propagate(branch, clues);

    if (result === 'solved') {
      count++;
      if (count >= search.limit) return count;
    } else if (result === 'stuck') {
      count += countSolutions(branch, clues, search);
      if (count >= search.limit) return count;
    }
    // contradiction: skip
  }

  return count;
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Fast bitboard-based solution analysis for grids up to 32×32.
 * Returns null if the grid is too large (caller should fall back).
 */
export function fastAnalyzeSolutions(
  puzzle: Puzzle,
  options: SolutionAnalysisOptions = {},
): SolutionAnalysisResult | null {
  if (puzzle.rows > 32 || puzzle.cols > 32) return null;

  const { clues } = puzzle;
  const limit = options.limit ?? 2;

  const grid = createBitGrid(puzzle.rows, puzzle.cols);
  const result = propagate(grid, clues);

  if (result === 'contradiction') {
    return { count: 0, complete: true, aborted: false, exploredStates: 0 };
  }
  if (result === 'solved') {
    return { count: 1, complete: true, aborted: false, exploredStates: 0 };
  }

  // Propagation got stuck — backtrack to count solutions
  const search: SearchState = {
    limit,
    maxStates: options.maxStates ?? Number.POSITIVE_INFINITY,
    exploredStates: 0,
    aborted: false,
  };

  const count = countSolutions(grid, clues, search);

  return {
    count,
    complete: !search.aborted,
    aborted: search.aborted,
    exploredStates: search.exploredStates,
  };
}

